/**
 * Keycloak bootstrap + auth context.
 *
 * Boot order, and why:
 *   1. GET /config.json          — we cannot construct Keycloak without it
 *   2. keycloak.init()           — login-required, so an anonymous visitor is
 *                                  redirected out of the app immediately
 *   3. GET /api/me               — identity, company, entitlements
 *
 * Nothing renders until all three succeed, which means every child component
 * can treat `me` as present and never has to write a loading branch.
 *
 * `checkLoginIframe: false` is not optional. With the login iframe off, no app
 * needs to be framed, which is what lets the router send
 * `frame-ancestors 'none'` and `X-Frame-Options: DENY` without breaking login.
 * Login is a top-level redirect, which CSP does not restrict.
 */
import { LanguageProvider, resolveLanguage, setDisplayLanguage, translate } from "./language";
import "./messages";
import Keycloak from "keycloak-js";

import { applyBrand } from "./brand";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { loadConfig, type PlatformConfig } from "./config";
import { createApi, ApiError, type ApiClient } from "./api";
import type { Me } from "./types";
import { Logo } from "@cqt/brand";

interface AuthContextValue {
  config: PlatformConfig;
  api: ApiClient;
  me: Me;
  /** Re-fetch /me — call after a grant changes so tiles update without F5. */
  refreshMe: () => Promise<void>;
  logout: () => void | Promise<void>;
  /** Open the configured identity service's account/security screen. */
  registerPasskey: () => Promise<void>;
  accountUrl: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type BootState =
  | { phase: "loading"; label: string }
  | { phase: "error"; label: string; detail: string }
  | { phase: "ready"; value: AuthContextValue };

/**
 * Full-page brand splash, on the dark plate in both themes.
 *
 * `brand` is optional because this screen renders BEFORE /config.json is
 * fetched — that is precisely what it is for. Without it the mark falls back to
 * neutral initials, which is the honest answer: at that moment the platform does
 * not yet know whose address this is. Once the config arrives, `applyBrand` has
 * already set the accent, so even the neutral mark is in the company's colour.
 */
function Splash({
  children,
  spinner = false,
  brand,
}: {
  children: ReactNode;
  spinner?: boolean;
  brand?: { wordmark?: string; logoUrl?: string };
}) {
  return (
    <div
      className="plate"
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        textAlign: "center",
      }}
    >
      <div style={{ display: "grid", justifyItems: "center", gap: 20, maxWidth: 460 }}>
        <Logo size={148} wordmark={brand?.wordmark} logoUrl={brand?.logoUrl} />
        {spinner ? <div className="spinner" aria-hidden="true" /> : null}
        {children}
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BootState>({ phase: "loading", label: "Starting" });
  // Keycloak.init() must run exactly once. React 19 StrictMode double-invokes
  // effects in development, and a second init() throws.
  const booted = useRef(false);
  // Whatever branding /config.json gave us, if boot got that far. A ref rather
  // than state: it is only read on a later render, so state would cost a
  // re-render purely to swap a logo.
  const splashBrand = useRef<{ wordmark?: string; logoUrl?: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;

    (async () => {
      let config: PlatformConfig;
      try {
        setState({ phase: "loading", label: "Loading configuration" });
        config = await loadConfig();
      } catch (err) {
        setState({
          phase: "error",
          label: "Configuration unavailable",
          detail:
            "The platform could not read /config.json. If you are running locally, " +
            "check that the router is up. " +
            (err instanceof Error ? err.message : String(err)),
        });
        return;
      }

      // Before Keycloak, and before anything renders: the sign-in screen is
      // the first thing a visitor sees and it should already be their company's.
      applyBrand(config.brand);
      setDisplayLanguage(resolveLanguage(config.language));
      splashBrand.current = {
        wordmark: config.brand.wordmark || config.companyName,
        logoUrl: config.brand.logoUrl,
      };

      let kc: Keycloak | undefined;
      let getToken: (() => Promise<string>) | undefined;
      if (config.authMode === "keycloak") {
        if (!config.keycloakUrl || !config.realm || !config.clientId) {
          throw new Error("legacy auth configuration is incomplete");
        }
        kc = new Keycloak({ url: config.keycloakUrl, realm: config.realm, clientId: config.clientId });
        try {
          setState({ phase: "loading", label: "Signing in" });
          await kc.init({ onLoad: "login-required", pkceMethod: "S256", checkLoginIframe: false });
        } catch (err) {
          setState({ phase: "error", label: "Sign-in unavailable", detail: `Could not reach Keycloak at ${config.keycloakUrl} (realm "${config.realm}"). ${err instanceof Error ? err.message : String(err)}` });
          return;
        }
        getToken = async () => {
          try { await kc!.updateToken(30); }
          catch { kc!.login(); throw new ApiError(401, "Session expired"); }
          return kc!.token ?? "";
        };
      }

      const stepUp = config.identityOrigin
        ? () => window.location.assign(`${config.apiBase}/auth/step-up?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`)
        : undefined;
      // A session can expire after the shell has loaded.  Redirecting here
      // keeps every app from rendering a raw 401 or leaving a mutation failed
      // when a fresh sign-in is the only safe recovery path.
      const signInAgain = config.authMode === "platform"
        ? () => {
            const returnTo = `${window.location.pathname}${window.location.search}` || "/";
            window.location.assign(`${config.apiBase}/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
          }
        : undefined;
      const api = createApi(config.apiBase, getToken, stepUp, signInAgain);

      const fetchMe = () => api.get<Me>("/me");

      let me: Me;
      try {
        setState({ phase: "loading", label: "Loading your account" });
        me = await fetchMe();
      } catch (err) {
        if (config.authMode === "platform" && err instanceof ApiError && err.status === 401) {
          signInAgain?.();
          return;
        }
        const detail =
          err instanceof ApiError && err.status === 403
            ? (config.authMode === "platform"
              ? "Your account does not currently have access. Contact your administrator."
              : "Your Keycloak login worked, but this account is not provisioned on the platform yet. An administrator needs to create your user record.")
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ phase: "error", label: "Account not available", detail });
        return;
      }

      const value: AuthContextValue = {
        config,
        api,
        me,
        refreshMe: async () => {
          const next = await fetchMe();
          setState((prev) =>
            prev.phase === "ready"
              ? { phase: "ready", value: { ...prev.value, me: next } }
              : prev,
          );
        },
        logout: config.authMode === "platform"
          ? async () => { await api.post("/auth/logout"); window.location.assign("/"); }
          : () => kc!.logout({ redirectUri: window.location.origin }),
        registerPasskey: async () => {
          if (config.authMode === "platform") {
            if (!config.identityOrigin) throw new Error("identityOrigin is not configured");
            window.location.assign(`${config.identityOrigin}/auth/`);
          } else {
            await kc!.login({ action: "webauthn-register-passwordless", redirectUri: window.location.href });
          }
        },
        accountUrl: config.authMode === "platform"
          ? `${config.identityOrigin}/auth/`
          : `${config.keycloakUrl}/realms/${encodeURIComponent(config.realm!)}/account`,
      };

      setState({ phase: "ready", value });
    })();
  }, []);

  if (state.phase === "loading") {
    return (
      <Splash spinner brand={splashBrand.current}>
        <p className="meta" style={{ letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {translate(state.label)}
        </p>
      </Splash>
    );
  }

  if (state.phase === "error") {
    return (
      <Splash brand={splashBrand.current}>
        <h1 style={{ color: "var(--cqt-bone)", fontSize: "1.4rem" }}>{translate(state.label)}</h1>
        <p style={{ color: "var(--cqt-ash-2)", fontSize: "0.92rem", lineHeight: 1.55 }}>
          {translate(state.detail)}
        </p>
        <button className="btn btn--primary" onClick={() => window.location.reload()}>
          {translate("Try again")}
        </button>
      </Splash>
    );
  }

  return <AuthContext.Provider value={state.value}><LanguageProvider api={state.value.api} me={state.value.me} refreshMe={state.value.refreshMe}>{children}</LanguageProvider></AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Convenience: the current user's level on a tool, or "none". */
export function useToolLevel(toolKey: string) {
  const { me } = useAuth();
  return me.tools.find((t) => t.key === toolKey)?.level ?? "none";
}

/** Stable callback identity for React Query keys etc. */
export function useApi(): ApiClient {
  return useAuth().api;
}

export function useMe(): Me {
  return useAuth().me;
}
